import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui/src/components/Toast";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import BaseItemType from "../components/itemtypes/question/BaseItemType";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import { handlePostAnswer } from "./StudySessionBottombarContainer/helpers";

interface Props {
	item: BaseItemSchema;
	response: ItemResponse;
	originalItemType: OriginalItemTypes;
	toggleShowReadMore?: (options?: { scrollToHighlight?: boolean }) => void;
}

const BaseItemTypeContainer = ({
	item,
	response,
	originalItemType,
	toggleShowReadMore,
}: Props) => {
	const { showToast } = useToast();

	const {
		currentExamContext: { projectId, organizationId, examId },
	} = useExamContext();

	const updateItemStates = useSessionStore((state) => state.updateItemStates);
	const itemStates = useSessionStore((state) => state.itemStates);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const setUserResponse = useAIFeedbackStore((state) => state.setUserResponse);
	const [startTime, setStartTime] = useState(new Date());
	const { index } = useLocalSearchParams<{
		index: string;
	}>();
	const sessionId = useSessionStore((state) => state.id);

	const shouldShowCrossout = useAppStore(
		(state) => state.sessionView?.showCrossOut,
	);

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const itemId = item.id!;

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		if (isSummativeTest) {
			setStartTime(new Date());
		}
	}, [itemId, isSummativeTest]);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse = userResponse?.[itemId];

	const itemState = itemStates?.get(itemId);
	const isAnswered = isSummativeTest
		? inReviewMode && item.answer != null
		: itemStates?.get(itemId)?.answered || item.answer != null;

	const choices = response.type === "choice" ? response.choices : [];
	const selectedChoiceIds =
		itemState?.selectedChoiceIds || item.answer?.selectedChoiceIds;
	const correctAnswerIds =
		itemState?.correctAnswerIds ||
		choices?.filter((choice) => choice.isCorrect).map((choice) => choice.id);

	const crossedOutChoiceIds = itemState?.crossedOutChoiceIds;

	const correctAnswerOrders =
		response.type === "choice"
			? response.correctResponse?.map((choice) => choice.id)
			: undefined;

	const isMultiSelect =
		response.type === "choice" && response.selectionType === "multiple";

	const shouldPreventSelection = (choiceId: string) => {
		return (
			isMultiSelect &&
			response.maxCorrectChoices &&
			selectedChoiceIds &&
			selectedChoiceIds.length >= response.maxCorrectChoices &&
			!selectedChoiceIds.includes(choiceId)
		);
	};

	const handleSelectChoice = (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => {
		if (shouldPreventSelection(choiceId) && response.type === "choice") {
			showToast({
				message: `Maximum limit reached. You can only select up to ${response.maxCorrectChoices} answers.`,
				duration: 500,
			});
			return;
		}

		trackCustomEvent({
			eventName: crossingOut
				? events.sessionItemCrossedOut
				: events.sessionItemOptionSelected,
			choiceId,
			itemId: itemId,
			sessionId: sessionId,
			isSummativeTest: isSummativeTest ? "true" : "false",
		});

		updateItemStates({
			itemId,
			selectedChoiceId: choiceId,
			multiSelect: isMultiSelect,
			crossOut: crossingOut,
			currentOrders,
		});
		if (isSummativeTest) {
			handlePostAnswer({
				state: useSessionStore.getState(),
				startTime,
				projectId,
				organizationId,
				examId,
				submitAnswer: true,
				currentItemIndex,
			});
		}
	};

	const handleHighlightedTextPress = () => {
		toggleShowReadMore?.({ scrollToHighlight: true });
	};

	const handleUpdateFormStates = (formStates: Record<string, string>) => {
		setUserResponse(itemId, formStates);
	};

	return (
		<BaseItemType
			item={item}
			originalItemType={originalItemType}
			selectedChoiceIds={selectedChoiceIds}
			correctAnswerIds={correctAnswerIds}
			crossedOutChoiceIds={crossedOutChoiceIds}
			isAnswered={isAnswered}
			inReviewMode={inReviewMode}
			currentUserResponse={currentUserResponse}
			isSummativeTest={isSummativeTest}
			shouldShowCrossout={shouldShowCrossout}
			onSelectChoice={handleSelectChoice}
			toggleShowReadMore={toggleShowReadMore}
			correctAnswerOrders={correctAnswerOrders}
			handleHighlightedTextPress={handleHighlightedTextPress}
			updateFormStates={handleUpdateFormStates}
		/>
	);
};

export default BaseItemTypeContainer;
